const STYLES = [
  { key: "classic", label: "Classic White", css: { color: "#fff", WebkitTextStroke: "1px #000", textShadow: "0 2px 4px #000" } },
  { key: "yellow", label: "Bold Yellow", css: { color: "#ffd60a", WebkitTextStroke: "1px #000", textShadow: "0 2px 4px #000" } },
  { key: "neon", label: "Neon Green", css: { color: "#39ff14", WebkitTextStroke: "1px #000", textShadow: "0 2px 4px #000" } },
];
const FONTS = [
  ["classic", "Classic", "Arial, Helvetica, sans-serif"],
  ["poppins", "Poppins", "'Poppins', sans-serif"],
  ["anton", "Anton", "'Anton', sans-serif"],
  ["pacifico", "Pacifico", "'Pacifico', cursive"],
];
const SIZES = [["small", "Small", 14], ["medium", "Medium", 18], ["large", "Large", 23]];
const POSITIONS = [["upper", "Upper", "30%"], ["middle", "Middle", "50%"], ["lower", "Lower", "72%"]];

const LOOK_ON = {
  classic: "border-white bg-white text-black",
  yellow: "border-[#ffd60a] bg-[#ffd60a] text-black",
  neon: "border-[#39ff14] bg-[#39ff14] text-black",
};

const LEVI_ON = "border-[#1e3aaa] bg-[#1e3aaa] text-white";

const Pill = ({ on, onClick, children, testid, onClass }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-full px-3 py-1.5 text-xs font-extrabold border-2 whitespace-nowrap focus:outline-none transition-colors ${on ? (onClass || "border-white bg-white text-black") : "border-white/20 text-white/75 hover:border-white/50"}`}>
    {children}
  </button>
);

const Row = ({ label, children }) => (
  <div>
    <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#a9d79b] mb-1.5">{label}</p>
    <div className="flex flex-wrap gap-1.5">{children}</div>
  </div>
);

// Auto Captions look for the whole video: style, size, position — with a live sample.
export default function CaptionStylePicker({ value, onChange }) {
  const v = { style: "classic", size: "medium", pos: "lower", color: "#ff4fd8", font: "classic", ...(value || {}) };
  const custom = v.style === "custom";
  const st = custom
    ? { css: { color: v.color, WebkitTextStroke: "1px #000", textShadow: "0 2px 4px #000" } }
    : STYLES.find((x) => x.key === v.style) || STYLES[0];
  const ff = (FONTS.find((x) => x[0] === v.font) || FONTS[0])[2];
  const fs = (SIZES.find((x) => x[0] === v.size) || SIZES[1])[2];
  const top = (POSITIONS.find((x) => x[0] === v.pos) || POSITIONS[2])[2];
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="sms-card sms-tweed p-3 mb-4 grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3" data-testid="cc-style-picker">
      <div className="space-y-2.5">
        <h3 className="text-sm font-extrabold text-white flex items-center gap-2">
          <span className="px-1 rounded-[3px] text-[10px] leading-tight border border-white">CC</span> Auto Caption Style
        </h3>
        <Row label="Look">
          {STYLES.map((x) => <Pill key={x.key} on={v.style === x.key} onClass={LOOK_ON[x.key]} onClick={() => set("style", x.key)} testid={`cc-style-${x.key}`}>{x.label}</Pill>)}
          <span className="inline-flex items-center gap-1.5">
            <button type="button" onClick={() => set("style", "custom")} data-testid="cc-style-custom"
              className="rounded-full px-3 py-1.5 text-xs font-extrabold border-2 whitespace-nowrap focus:outline-none transition-colors"
              style={custom ? { background: v.color, borderColor: v.color, color: "#000" } : { borderColor: "rgba(255,255,255,.2)", color: "rgba(255,255,255,.75)" }}>
              Custom Color
            </button>
            <label title="Pick any caption colour" data-testid="cc-color-wheel"
              className="relative w-8 h-8 rounded-full cursor-pointer border-2 border-white/80 shadow-[0_0_10px_rgba(255,255,255,.25)] transition-transform hover:scale-110"
              style={{ background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)" }}>
              <span className="absolute inset-[9px] rounded-full border border-black/40" style={{ background: v.color }} />
              <input type="color" value={v.color} data-testid="cc-color-input"
                onChange={(e) => onChange({ ...v, style: "custom", color: e.target.value })}
                className="absolute inset-0 opacity-0 cursor-pointer" />
            </label>
          </span>
        </Row>
        <Row label="Font">{FONTS.map(([k, l, f]) => <Pill key={k} on={v.font === k} onClass={LEVI_ON} onClick={() => set("font", k)} testid={`cc-font-${k}`}><span style={{ fontFamily: f, fontWeight: k === "pacifico" || k === "anton" ? 400 : 800 }}>{l}</span></Pill>)}</Row>
        <Row label="Size">{SIZES.map(([k, l]) => <Pill key={k} on={v.size === k} onClass={LEVI_ON} onClick={() => set("size", k)} testid={`cc-size-${k}`}>{l}</Pill>)}</Row>
        <Row label="Position">{POSITIONS.map(([k, l]) => <Pill key={k} on={v.pos === k} onClass={LEVI_ON} onClick={() => set("pos", k)} testid={`cc-pos-${k}`}>{l}</Pill>)}</Row>
      </div>
      <div className="relative rounded-xl overflow-hidden aspect-video sm:aspect-auto sm:h-full min-h-[112px] bg-gradient-to-br from-[#2c3e2a] via-[#1b2a3a] to-[#3a2a1b]" data-testid="cc-style-preview">
        <span className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-max max-w-[88%] text-center font-extrabold leading-tight transition-all duration-200"
          style={{ top, fontSize: fs, fontFamily: ff, fontWeight: v.font === "pacifico" || v.font === "anton" ? 400 : 800, ...st.css }}>
          This is your custom caption
        </span>
      </div>
    </div>
  );
}
